import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import UndoIcon from '@mui/icons-material/Undo';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import creditImportService from '../services/creditImportService';
import type {
  CreditImportBatch,
  CreditImportErrorBody,
  ExcelImportCommitRow,
  ExcelImportIssue,
  ExcelImportPreview,
  ExcelImportSkippedRange,
} from '../services/creditImportService';
import { getApiErrorInfo } from '../services/api';
import { track } from '../services/analytics';
import ImportColumnMappingStrip from '../components/credit-import/ImportColumnMappingStrip';
import ImportSummaryBand from '../components/credit-import/ImportSummaryBand';
import ImportRowList from '../components/credit-import/ImportRowList';
import RecentImportsList from '../components/credit-import/RecentImportsList';
import ConnectedSourcesList from '../components/credit-import/ConnectedSourcesList';
import {
  DEFAULT_FILE_IMPORT_SOURCE,
  defaultFileImportSource,
  fileImportSource,
  toolFileImportSources,
  type FileImportSource,
  type FileImportSourceId,
} from '../components/credit-import/fileImportSources';
import { useCreditImports } from '../hooks/useCreditImports';
import ConfirmDialog from '../components/common/ConfirmDialog';
import {
  countRows,
  effectiveHours,
  formatHours,
  hoursByCategory,
  isoToday,
  pluralActivities,
  rowDomId,
  rowStatus,
  sortForReview,
  toDraft,
  unresolvedColumns,
  validateRow,
  type ColumnMapping,
  type ImportRowDraft,
  type ValidationContext,
} from '../utils/creditImportValidation';

/** State handed back to CPE Reporting so it can show the success snackbar with Undo. */
export interface ImportDoneState {
  importedBatch: { id: string; rowsImported: number; mode: 'self' | 'tenant' };
}

type Step = 'upload' | 'review' | 'done';

const MAX_FILE_BYTES = 10 * 1024 * 1024;
/** One review draft per browser session; survives refresh and Back. */
const DRAFT_KEY = 'cpe-credit-import-draft';
const STEPS: { key: Step; label: string }[] = [
  { key: 'upload', label: 'Upload' },
  { key: 'review', label: 'Review & fix' },
  { key: 'done', label: 'Done' },
];

/** Label/value facts: the label column is the scan path, one fact per line. */
const FACTS_SX = {
  m: 0,
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'max-content 1fr' },
  columnGap: 3,
  rowGap: { xs: 1, sm: 0.75 },
  '& dt': { fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap' },
  '& dd': { m: 0, color: 'text.secondary' },
} as const;

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'contents' }}>
      <Typography component="dt" variant="body2">{label}</Typography>
      <Typography component="dd" variant="body2">{value}</Typography>
    </Box>
  );
}

/** A source's guidance as the registry describes it: intro, export steps, note. Firm mode adds the email rule. */
function SourceGuide({ source, firm }: { source: FileImportSource; firm: boolean }) {
  return (
    <>
      {source.intro && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          {source.intro}
        </Typography>
      )}
      {source.steps && (
        <Box component="ol" sx={{ m: 0, mb: 1.5, pl: 2.5, color: 'text.secondary', '& li': { mb: 0.5 } }}>
          {source.steps.map((text) => (
            <Typography key={text} component="li" variant="body2">
              {text}
            </Typography>
          ))}
        </Box>
      )}
      {firm && source.firmNote && (
        <Box component="dl" sx={{ ...FACTS_SX, mb: 1.5 }} data-testid={`credit-import-source-facts-${source.id}`}>
          <Fact label="Emails" value={source.firmNote} />
        </Box>
      )}
      {source.note && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          {source.note}
        </Typography>
      )}
    </>
  );
}

interface ReviewDraft {
  preview: ExcelImportPreview;
  rows: ImportRowDraft[];
  columnMapping: ColumnMapping;
  fixesMade: number;
  savedAt: string;
}

function readDraft(key: string, tenantId: string | null): ReviewDraft | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ReviewDraft;
    if (!parsed || !parsed.preview || !Array.isArray(parsed.rows)) return null;
    // A draft belongs to one scope: never restore a firm review into a personal one or vice versa.
    return (parsed.preview.tenantId ?? null) === tenantId ? parsed : null;
  } catch {
    return null;
  }
}

/** "rows 1–6 (above the header), row 12 (no title or date), rows 13–16 (after the table)" */
function describeSkipped(skipped: ExcelImportSkippedRange[]): string {
  return skipped
    .map((s) => `${s.fromRow === s.toRow ? `row ${s.fromRow}` : `rows ${s.fromRow}–${s.toRow}`} (${s.reason})`)
    .join(', ');
}

function writeDraft(key: string, draft: ReviewDraft | null) {
  try {
    if (draft) window.sessionStorage.setItem(key, JSON.stringify(draft));
    else window.sessionStorage.removeItem(key);
  } catch {
    // Storage full or blocked: the review still works, it just won't survive a refresh.
  }
}

interface Props {
  /** Firm-wide import for this tenant (its tenant admin, or a system admin). Absent: the user's own history. */
  tenantId?: string;
  /** Where the back link and the Done step's button lead. Default: CPE Reporting. */
  backTo?: { to: string; label: string };
  /** The hosting admin layout already renders the breadcrumb and page title. */
  hideHeader?: boolean;
  /**
   * Firm mode: the firm's name, shown in the title, on the review line and on Done so a system admin
   * with several tenants open never commits to the wrong one.
   */
  scopeName?: string;
}

const SELF_BACK = { to: '/app/cpe-history', label: 'CPE Reporting' };

/**
 * Import of CPE credits: Upload → Review & fix → Done. The Upload step holds everything in one place:
 * connected sources (acted on in place, personal history only), the file upload, the sheet setup for the
 * default source, and a disclosure per tool from the registry in `components/credit-import/fileImportSources.ts`
 * ("Exporting from LCvista?"). Nothing is chosen up front: the server detects the source from the file.
 * Strict: nothing imports while a row still needs attention or a column is undecided. Rows land as
 * ordinary editable manual credits under an undoable batch. With a tenantId this is the firm-wide import:
 * rows carry an Email that must match someone in the firm (or, for per-person tool exports, the email in
 * the file's name line). Design: docs/plans/excel-credit-import.md.
 */
export default function CpeHistoryImport({ tenantId, backTo, hideHeader = false, scopeName }: Props = {}) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const firm = Boolean(tenantId);
  const back = backTo ?? SELF_BACK;
  const draftKey = tenantId ? `${DRAFT_KEY}:tenant:${tenantId}` : DRAFT_KEY;

  // Lazy initializer: read and parse the stored draft once on mount, not on every render (it can be a 2,000-row JSON blob).
  const [initialDraft] = useState<ReviewDraft | null>(() => readDraft(draftKey, tenantId ?? null));
  const [step, setStep] = useState<Step>(initialDraft ? 'review' : 'upload');
  const [restored, setRestored] = useState(initialDraft !== null);
  const [parsing, setParsing] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Connected sources (PICPA now, CPAx later) head the Upload step for the person's own history only.
  const { imports: connected } = useCreditImports();

  const [preview, setPreview] = useState<ExcelImportPreview | null>(initialDraft?.preview ?? null);
  const [rows, setRows] = useState<ImportRowDraft[]>(initialDraft?.rows ?? []);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  /** Firm mode: rows whose edited email is being re-matched by the server. */
  const [pendingEmailRows, setPendingEmailRows] = useState<Set<number>>(() => new Set());
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>(initialDraft?.columnMapping ?? {});
  const [fixesMade, setFixesMade] = useState(initialDraft?.fixesMade ?? 0);
  const [confirmStartOver, setConfirmStartOver] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState<string | null>(null);
  const [serverRowIssues, setServerRowIssues] = useState<Record<number, ExcelImportIssue[]>>({});

  const [batch, setBatch] = useState<CreditImportBatch | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [undone, setUndone] = useState<number | null>(null);

  const [recent, setRecent] = useState<CreditImportBatch[]>([]);
  const [recentLoading, setRecentLoading] = useState(true);
  /** Which tool's export walkthrough is open under the sheet setup (one at a time; closed by default). */
  const [openTool, setOpenTool] = useState<FileImportSourceId | null>(null);
  const [snack, setSnack] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const committedRef = useRef(false);

  const loadRecent = useCallback(async () => {
    setRecentLoading(true);
    try {
      setRecent(await creditImportService.listBatches(tenantId));
    } catch {
      setRecent([]);
    } finally {
      setRecentLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void loadRecent();
  }, [loadRecent]);

  // -------------------------------------------------------------------------
  // Validation and derived review state
  // -------------------------------------------------------------------------

  const ctx = useMemo<ValidationContext | null>(
    () =>
      preview
        ? {
            mode: preview.mode,
            deliveryMethods: preview.deliveryMethods,
            categoryIds: new Set(preview.categories.map((c) => c.id)),
            columnMapping,
            today: isoToday(),
          }
        : null,
    [preview, columnMapping],
  );

  const issuesByRow = useMemo(() => {
    const out: Record<number, ExcelImportIssue[]> = {};
    if (!ctx) return out;
    for (const row of rows) {
      // While the server re-matches an edited email, the row is neither matched nor unmatched: hold the message.
      const own = validateRow(row, ctx).filter((i) => !(i.field === 'email' && pendingEmailRows.has(row.rowNumber)));
      const fromServer = serverRowIssues[row.rowNumber] ?? [];
      out[row.rowNumber] = fromServer.length > 0 ? [...own, ...fromServer.filter((s) => !own.some((o) => o.field === s.field))] : own;
    }
    return out;
  }, [rows, ctx, serverRowIssues, pendingEmailRows]);

  const counts = useMemo(() => countRows(rows, issuesByRow, columnMapping), [rows, issuesByRow, columnMapping]);
  const breakdown = useMemo(
    () => (preview ? hoursByCategory(rows, issuesByRow, columnMapping, preview.categories) : []),
    [rows, issuesByRow, columnMapping, preview],
  );
  const unresolved = useMemo(() => (preview ? unresolvedColumns(preview.columns, rows, columnMapping) : []), [preview, rows, columnMapping]);

  const blockedReason =
    pendingEmailRows.size > 0
      ? 'Checking an email…'
      : unresolved.length > 0
      ? `${unresolved.length} ${unresolved.length === 1 ? 'column needs' : 'columns need'} a decision`
      : counts.errors > 0
        ? `${counts.errors} ${counts.errors === 1 ? 'row needs' : 'rows need'} attention`
        : counts.ready === 0
          ? 'Nothing to import'
          : null;
  const canImport = step === 'review' && blockedReason === null && !committing;

  /** Work the user would lose: inline fixes, removals, or include/skip changes. */
  const dirty = fixesMade > 0 || rows.some((r) => r.removed || r.include !== !r.isDuplicate);

  // Persist the review while it is open so a refresh, an accidental Back, or coming back later lands here again.
  useEffect(() => {
    if (step !== 'review' || !preview) return;
    writeDraft(draftKey, { preview, rows, columnMapping, fixesMade, savedAt: new Date().toISOString() });
  }, [draftKey, step, preview, rows, columnMapping, fixesMade]);

  // Closing the tab is the one exit sessionStorage cannot survive.
  useEffect(() => {
    if (step !== 'review' || !dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [step, dirty]);

  // Abandon tracking: leaving the review step without committing.
  const reviewRef = useRef<{ rows: number; errors: number; mode: 'self' | 'tenant' } | null>(null);
  useEffect(() => {
    if (step === 'review' && preview) {
      reviewRef.current = { rows: rows.length, errors: counts.errors, mode: preview.mode };
    }
  }, [step, preview, rows.length, counts.errors]);
  useEffect(
    () => () => {
      if (reviewRef.current && !committedRef.current) {
        track('credit_import_abandoned', {
          mode: reviewRef.current.mode,
          rows: reviewRef.current.rows,
          errors_remaining: reviewRef.current.errors,
        });
      }
    },
    [],
  );

  // -------------------------------------------------------------------------
  // Upload
  // -------------------------------------------------------------------------

  const handleInput = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) void handleFile(file);
  };

  const [dragging, setDragging] = useState(false);
  // dragenter/dragleave fire for every child the cursor crosses; only the outermost pair matters.
  const dragDepth = useRef(0);
  const handleDragEnter = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    dragDepth.current += 1;
    if (!parsing) setDragging(true);
  };
  const handleDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };
  const handleDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (parsing) return;
    const file = event.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  /** Parse the workbook. The server detects the source from the file itself (our template, an LCvista export, or neither). */
  const handleFile = async (file: File) => {
    setUploadError(null);
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setUploadError('Only .xlsx files are supported. Save as Excel Workbook (.xlsx) and try again.');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setUploadError('The file is larger than 10 MB. Remove extra sheets or images and try again.');
      return;
    }

    setParsing(true);
    try {
      const result = await creditImportService.parse(file, tenantId);
      setPreview(result);
      setRows(result.rows.map(toDraft));
      setColumnMapping({});
      setFixesMade(0);
      setServerRowIssues({});
      setCommitError(null);
      setRestored(false);
      committedRef.current = false;
      track('credit_import_parsed', {
        mode: result.mode,
        source: result.source,
        detected_source: result.detectedSource ?? null,
        rows: result.rows.length,
        errors: result.errors,
        duplicates: result.duplicates,
        unrecognized_columns: result.columns.filter((c) => c.kind === 'unrecognized' && c.hasNumbers).length,
      });
      setStep('review');
    } catch (error) {
      const info = getApiErrorInfo(error);
      setUploadError(info.message || 'That file could not be read.');
    } finally {
      setParsing(false);
    }
  };

  const handleTemplate = async () => {
    try {
      await creditImportService.downloadTemplate(tenantId);
    } catch {
      setSnack({ message: 'Could not download the template. Try again.', severity: 'error' });
    }
  };

  // -------------------------------------------------------------------------
  // Review
  // -------------------------------------------------------------------------

  /** Firm mode: ask the server who an edited email belongs to. Only the server may say (Q11: never reveal other firms' users). */
  const resolveEmail = useCallback(
    async (rowNumber: number, email: string | null) => {
      if (!tenantId) return;
      const trimmed = email?.trim() ?? '';
      if (!trimmed) return; // blank is a validation message, not a lookup
      setPendingEmailRows((prev) => new Set(prev).add(rowNumber));
      try {
        const [match] = await creditImportService.resolveEmails(tenantId, [trimmed]);
        setRows((prev) =>
          prev.map((r) => (r.rowNumber === rowNumber && (r.email?.trim() ?? '') === trimmed ? { ...r, userId: match?.userId ?? null } : r)),
        );
      } catch {
        setRows((prev) =>
          prev.map((r) =>
            r.rowNumber === rowNumber
              ? { ...r, serverIssues: [...r.serverIssues.filter((i) => i.field !== 'email'), { field: 'email', message: "Couldn't check this email. Edit it again to retry." }] }
              : r,
          ),
        );
      } finally {
        setPendingEmailRows((prev) => {
          const next = new Set(prev);
          next.delete(rowNumber);
          return next;
        });
      }
    },
    [tenantId],
  );

  const handleRowChange = useCallback(
    (draft: ImportRowDraft) => {
      const before = rowsRef.current.find((r) => r.rowNumber === draft.rowNumber);
      const emailChanged = firm && before !== undefined && (before.email?.trim() ?? '') !== (draft.email?.trim() ?? '');
      // An edited email is unmatched until the server says otherwise, and its old verdict no longer applies.
      const next = emailChanged ? { ...draft, userId: null, serverIssues: draft.serverIssues.filter((i) => i.field !== 'email') } : draft;
      setRows((prev) => prev.map((r) => (r.rowNumber === next.rowNumber ? next : r)));
      setServerRowIssues((prev) => {
        if (!prev[next.rowNumber]) return prev;
        const copy = { ...prev };
        delete copy[next.rowNumber];
        return copy;
      });
      setFixesMade((n) => n + 1);
      if (emailChanged) void resolveEmail(next.rowNumber, next.email ?? null);
    },
    [firm, resolveEmail],
  );

  const handleToggleInclude = useCallback((rowNumber: number, include: boolean) => {
    setRows((prev) => prev.map((r) => (r.rowNumber === rowNumber ? { ...r, include } : r)));
  }, []);

  const handleRemoveRow = useCallback((rowNumber: number) => {
    setRows((prev) => prev.map((r) => (r.rowNumber === rowNumber ? { ...r, removed: true } : r)));
  }, []);

  const handleRestoreRow = useCallback((rowNumber: number) => {
    setRows((prev) => prev.map((r) => (r.rowNumber === rowNumber ? { ...r, removed: false } : r)));
  }, []);

  const jumpTo = (predicate: (row: ImportRowDraft, issues: ExcelImportIssue[]) => boolean) => {
    const target = sortForReview(rows, issuesByRow).find((r) => predicate(r, issuesByRow[r.rowNumber] ?? []));
    if (!target) return;
    document.getElementById(rowDomId(target.rowNumber))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleCommit = async () => {
    if (!preview || !canImport) return;
    setCommitting(true);
    setCommitError(null);
    const included = rows.filter((r) => rowStatus(issuesByRow[r.rowNumber] ?? [], r.include, r.removed) === 'ready');
    const commitRows: ExcelImportCommitRow[] = included.map((r) => ({
      rowNumber: r.rowNumber,
      email: r.email ?? null,
      activityTitle: r.activityTitle ?? null,
      dateEarned: r.dateEarned ?? null,
      providerName: r.providerName ?? null,
      nasbaSponsorId: r.nasbaSponsorId ?? null,
      deliveryMethod: r.deliveryMethod ?? null,
      notes: r.notes ?? null,
      hours: effectiveHours(r, columnMapping),
    }));
    const numericMapping: Record<string, number> = {};
    for (const [header, value] of Object.entries(columnMapping)) {
      if (typeof value === 'number') numericMapping[header] = value;
    }
    try {
      const result = await creditImportService.commit({
        batchId: preview.batchId,
        tenantId: preview.tenantId ?? null,
        fileName: preview.fileName,
        sheetName: preview.sheetName,
        source: preview.source,
        columnMapping: numericMapping,
        rows: commitRows,
        rowsSkipped: rows.length - included.length,
      });
      committedRef.current = true;
      writeDraft(draftKey, null);
      setBatch(result);
      track('credit_import_committed', {
        mode: preview.mode,
        rows_imported: result.rowsImported,
        rows_skipped: result.rowsSkipped,
        fixes_made: fixesMade,
      });
      setStep('done');
      void loadRecent();
    } catch (error) {
      const info = getApiErrorInfo(error);
      const body = (error as { response?: { data?: CreditImportErrorBody } })?.response?.data;
      if (body?.rows && body.rows.length > 0) {
        const next: Record<number, ExcelImportIssue[]> = {};
        for (const r of body.rows) next[r.rowNumber] = r.issues;
        setServerRowIssues(next);
        setCommitError('Some rows still need attention.');
        const first = body.rows[0]?.rowNumber;
        if (first !== undefined) {
          window.setTimeout(() => document.getElementById(rowDomId(first))?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
        }
      } else {
        setCommitError(info.message || 'The import failed. Try again.');
      }
    } finally {
      setCommitting(false);
    }
  };

  const startOver = () => {
    writeDraft(draftKey, null);
    setConfirmStartOver(false);
    setRestored(false);
    setFixesMade(0);
    setStep('upload');
    setPreview(null);
    setRows([]);
    setColumnMapping({});
    setServerRowIssues({});
    setCommitError(null);
  };

  /** Firm mode, after a per-person file: straight back to Upload for the next person's file. */
  const importAnother = () => {
    setBatch(null);
    setUndone(null);
    committedRef.current = false;
    startOver();
  };

  // -------------------------------------------------------------------------
  // Done / undo
  // -------------------------------------------------------------------------

  const undoBatch = async (target: CreditImportBatch): Promise<boolean> => {
    try {
      const result = await creditImportService.undo(target.id);
      track('credit_import_undone', {
        mode: target.targetUserId ? 'self' : 'tenant',
        rows_removed: result.rowsRemoved,
        minutes_since_import: Math.max(0, Math.round((Date.now() - new Date(target.uploadedAt).getTime()) / 60000)),
      });
      void loadRecent();
      return true;
    } catch (error) {
      const body = (error as { response?: { data?: CreditImportErrorBody } })?.response?.data;
      setSnack({
        message: body?.message || getApiErrorInfo(error).message || 'Could not undo this import.',
        severity: 'error',
      });
      return false;
    }
  };

  const handleUndoDone = async () => {
    if (!batch) return;
    setUndoing(true);
    const ok = await undoBatch(batch);
    if (ok) setUndone(batch.rowsImported);
    setUndoing(false);
  };

  const finish = () => {
    // Only CPE Reporting knows how to show the success snackbar with Undo; admin pages just go back.
    if (!firm && batch && undone === null) {
      const state: ImportDoneState = {
        importedBatch: { id: batch.id, rowsImported: batch.rowsImported, mode: preview?.mode ?? 'self' },
      };
      navigate(back.to, { state });
    } else {
      navigate(back.to);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const setup = defaultFileImportSource();
  const tools = toolFileImportSources();
  const showConnected = !firm && connected.length > 0;
  /** "read as …" only earns its place when it is news: the parser recognized a tool's export. */
  const showReadAs = preview !== null && fileImportSource(preview.source).id !== DEFAULT_FILE_IMPORT_SOURCE;
  /** Done, firm mode: how many people the imported rows belong to. */
  const peopleImported = useMemo(() => {
    if (!firm) return 0;
    const ids = new Set<string>();
    for (const r of rows) {
      if (r.userId && rowStatus(issuesByRow[r.rowNumber] ?? [], r.include, r.removed) === 'ready') ids.add(r.userId);
    }
    return ids.size;
  }, [firm, rows, issuesByRow]);

  return (
    <Box sx={step === 'review' ? { display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 160px)' } : undefined}>
      {!hideHeader && (
        <>
          <Link component={RouterLink} to={back.to} underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
            <ArrowBackIcon fontSize="small" /> {back.label}
          </Link>
          <Typography variant="h4" sx={{ mb: 2 }}>
            {scopeName ? `Import credits for ${scopeName}` : 'Import credits'}
          </Typography>
        </>
      )}
      <Stepper activeStep={STEPS.findIndex((s) => s.key === step)} sx={{ maxWidth: 760, mb: 4 }} aria-label="Import progress">
        {STEPS.map((s) => (
          <Step key={s.key} completed={STEPS.findIndex((x) => x.key === step) > STEPS.findIndex((x) => x.key === s.key)}>
            <StepLabel>{s.label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {step === 'upload' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 760 }}>
          {/* Connected sources act in place, so they sit above the file path rather than behind a choice. */}
          {showConnected && <ConnectedSourcesList connected={connected} />}

          <Box component="section" aria-labelledby={showConnected ? 'import-file-heading' : 'credit-import-upload-heading'}>
            {/* The label only earns its place when there is a Connected section to tell the file path apart from. */}
            {showConnected && (
              <Typography id="import-file-heading" variant="overline" component="h2" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
                From a file
              </Typography>
            )}

            {/* "Give us the file": one heading that names the action, the limits, one button.
                The panel is the drop target; a faint dashed edge at rest says so before anyone drags. */}
            <Box
              role="group"
              aria-labelledby="credit-import-upload-heading"
              data-testid="credit-import-dropzone"
              onDragEnter={handleDragEnter}
              onDragOver={(e) => e.preventDefault()}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                p: 3,
                borderRadius: 1,
                bgcolor: dragging ? 'tint.primaryMuted' : 'tint.primarySoft',
                border: '1.5px dashed',
                borderColor: dragging ? 'primary.main' : 'tint.primaryMuted',
                transition: 'background-color 150ms ease-out, border-color 150ms ease-out',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 260 }}>
                <Typography id="credit-import-upload-heading" variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {dragging ? 'Drop to upload' : 'Upload a spreadsheet'}
                </Typography>
                <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Excel workbook (.xlsx), up to 10 MB and 2,000 rows
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={parsing ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                disabled={parsing}
                onClick={() => fileInputRef.current?.click()}
              >
                {parsing ? 'Reading…' : 'Choose file'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                hidden
                data-testid="credit-import-file"
                onChange={handleInput}
              />
              {/* The panel is what failed, so the message stays inside it, on its own row. */}
              {uploadError && (
                <Alert severity="error" sx={{ flexBasis: '100%' }}>
                  {uploadError}
                </Alert>
              )}
            </Box>
          </Box>

          {/* "Make the file": the sheet setup for the default source, under the action. Plain, no card:
              reference text, not a feature in itself. The generic sheet rules fold away once the person has imported before. */}
          <Box component="section" aria-labelledby="credit-import-instructions-heading">
            <Typography id="credit-import-instructions-heading" variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {setup.heading}
            </Typography>
            <SourceGuide source={setup} firm={firm} />
            {setup.template && (
              <Button variant="outlined" startIcon={<DownloadIcon />} disabled={parsing} onClick={() => void handleTemplate()}>
                Download template
              </Button>
            )}
            {/* Three lines, always in view: reference text this short is cheaper to read past than to find behind a toggle,
                and the step keeps one shape whether or not the person has imported before. */}
            {setup.rules && (
              <Box component="dl" sx={{ ...FACTS_SX, mt: 2 }} data-testid="credit-import-setup-rules">
                <Fact label="Layout" value="One row per activity. Hours go under a column for each field of study." />
                <Fact label="Dates" value="Excel dates, or text like 2025-03-15 or 3/15/2025." />
                <Fact label="Hours" value="In tenths: 1.5, not 1.25." />
              </Box>
            )}
          </Box>

          {/* One disclosure per tool we recognize: its export walkthrough, folded until asked for. The file
              itself tells the parser what it is, so this is help, not a choice. */}
          {tools.map((tool) => {
            const open = openTool === tool.id;
            const panelId = `credit-import-tool-${tool.id}`;
            return (
              <Box key={tool.id} component="section" aria-labelledby={`${panelId}-toggle`}>
                <Button
                  id={`${panelId}-toggle`}
                  variant="text"
                  size="small"
                  aria-expanded={open}
                  aria-controls={open ? panelId : undefined}
                  endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => setOpenTool(open ? null : tool.id)}
                  sx={{ ml: '-5px' }}
                >
                  Exporting from {tool.name}?
                </Button>
                {open && (
                  <Box id={panelId} sx={{ mt: 1 }}>
                    <SourceGuide source={tool} firm={firm} />
                  </Box>
                )}
              </Box>
            );
          })}

          <RecentImportsList
            batches={recent}
            loading={recentLoading}
            onUndo={async (b) => {
              const ok = await undoBatch(b);
              if (ok) setSnack({ message: `Removed ${pluralActivities(b.rowsImported)} from “${b.fileName}”.`, severity: 'success' });
              return ok;
            }}
          />
        </Box>
      )}

      {step === 'review' && preview && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }} data-testid="credit-import-read-as">
            {preview.fileName}
            {scopeName && ` · for ${scopeName}`}
            {showReadAs && ` · read as ${fileImportSource(preview.source).readAs}`}
            {` · ${rows.length} ${rows.length === 1 ? 'row' : 'rows'}.`}
            {preview.preambleEmail && ` Every row is for ${preview.preambleEmail}.`}
            {restored && ' Restored from your earlier session.'}
          </Typography>
          {preview.skipped && preview.skipped.length > 0 && (
            // The sheet had more than the activity list (a name line, a requirements table, totals): say what was left out.
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: -1.5, mb: 2 }} data-testid="credit-import-skipped">
              Left out: {describeSkipped(preview.skipped)}.
            </Typography>
          )}

          <ImportSummaryBand
            counts={counts}
            breakdown={breakdown}
            unresolved={unresolved}
            onJumpToExceptions={() => jumpTo((r, issues) => rowStatus(issues, r.include, r.removed) === 'error')}
            onJumpToDuplicates={() => jumpTo((r, issues) => rowStatus(issues, r.include, r.removed) === 'skipped')}
          />

          <ImportColumnMappingStrip
            columns={preview.columns}
            categories={preview.categories}
            rows={rows}
            mapping={columnMapping}
            onChange={setColumnMapping}
          />

          <Box sx={{ flex: 1, minHeight: 0, pb: 2 }}>
            <ImportRowList
              mode={preview.mode}
              rows={rows}
              issuesByRow={issuesByRow}
              categories={preview.categories}
              deliveryMethods={preview.deliveryMethods}
              columnMapping={columnMapping}
              pendingEmailRows={pendingEmailRows}
              onRowChange={handleRowChange}
              onToggleInclude={handleToggleInclude}
              onRemoveRow={handleRemoveRow}
              onRestoreRow={handleRestoreRow}
            />
          </Box>

          {/* Sticky footer inside the content column: counts restated + the one action, with its reason when blocked */}
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              mx: -3,
              px: 3,
              py: 1.5,
              bgcolor: 'surface.raised',
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              zIndex: 1,
            }}
          >
            {/* The commit failed from this footer, so the message lands here, where the click was, not above a long list. */}
            {commitError && (
              <Alert severity="error" sx={{ flexBasis: '100%', py: 0 }}>
                {commitError}
              </Alert>
            )}
            <Typography variant="body2" sx={{ flex: 1, minWidth: 220, color: 'text.secondary' }}>
              <strong>{counts.ready}</strong> ready · <strong>{formatHours(counts.readyHours)}</strong> hours
              {counts.skipped > 0 && <> · <strong>{counts.skipped}</strong> skipped</>}
              {counts.errors > 0 && <> · <strong>{counts.errors}</strong> need attention</>}
            </Typography>
            <Button variant="text" onClick={() => (dirty ? setConfirmStartOver(true) : startOver())} disabled={committing}>
              Start over
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {blockedReason && !committing && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }} id="credit-import-blocked-reason">
                  {blockedReason}
                </Typography>
              )}
              <Button
                variant="contained"
                disabled={!canImport}
                aria-describedby={blockedReason ? 'credit-import-blocked-reason' : undefined}
                startIcon={committing ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                onClick={() => void handleCommit()}
              >
                Import {pluralActivities(counts.ready)} ({formatHours(counts.readyHours)} hours)
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {step === 'done' && batch && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 760 }}>
          {/* Achieved state: the success family, not the action tint the upload step uses */}
          <Box sx={{ p: 3, borderRadius: 1, bgcolor: undone !== null ? 'surface.sunken' : 'status.completed.bg', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 240 }}>
              {/* The number the button promised (hours) is the number the end restates. */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {undone !== null
                  ? `Removed ${pluralActivities(undone)}`
                  : `Imported ${pluralActivities(batch.rowsImported)} · ${formatHours(counts.readyHours)} hours${
                      firm && peopleImported > 0 ? ` for ${peopleImported} ${peopleImported === 1 ? 'person' : 'people'}` : ''
                    }`}
              </Typography>
              {/* The title already says what happened; this line only adds where it came from (and, for a firm, where it went). */}
              {undone === null && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {`From ${batch.fileName}${batch.rowsSkipped > 0 ? ` · ${batch.rowsSkipped} skipped` : ''}${scopeName ? ` · ${scopeName}` : ''}.`}
                  {firm && ' Each person sees them in CPE Reporting.'}
                </Typography>
              )}
              {undone === null && breakdown.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }} aria-label="Imported hours by field of study">
                  {breakdown.map(({ category, hours }) => (
                    <Chip
                      key={category.id}
                      size="small"
                      label={`${category.name} ${formatHours(hours)}`}
                      sx={{ bgcolor: 'tint.primarySoft', color: 'primary.dark', fontWeight: 600 }}
                    />
                  ))}
                </Box>
              )}
            </Box>
            {undone === null && (
              <Button
                variant="outlined"
                startIcon={undoing ? <CircularProgress size={16} color="inherit" /> : <UndoIcon />}
                disabled={undoing}
                onClick={() => void handleUndoDone()}
              >
                Undo this import
              </Button>
            )}
            {/* Firm migrations from per-person exports are one file per person: keep the loop short. */}
            {firm && (
              <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={importAnother}>
                Import another file
              </Button>
            )}
            <Button variant="contained" onClick={finish}>
              Back to {back.label}
            </Button>
          </Box>
        </Box>
      )}

      <ConfirmDialog
        open={confirmStartOver}
        title="Discard this review?"
        message={`${fixesMade > 0 ? `${fixesMade} ${fixesMade === 1 ? 'fix' : 'fixes'} ` : 'Your changes '}will be lost.`}
        confirmLabel="Discard"
        onConfirm={startOver}
        onClose={() => setConfirmStartOver(false)}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnack(null)} severity={snack?.severity ?? 'success'} sx={{ width: '100%' }}>
          {snack?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
